/**
 * Full-screen loading while the app server is prepared for login.
 * Does not expose VM status details (TERMINATED, etc.).
 */
import React from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 420,
    textAlign: 'center',
  },
  progress: {
    margin: theme.spacing(2),
  },
  message: {
    marginTop: theme.spacing(1),
  },
}));

interface ServerPreparingPageProps {
  message?: string;
  error?: string | null;
  onRetry?: () => void;
}

const ServerPreparingPage: React.FC<ServerPreparingPageProps> = ({
  message = 'Please wait while we prepare your session...',
  error,
  onRetry,
}) => {
  const classes = useStyles();

  return (
    <Container className={classes.container}>
      <Paper className={classes.paper} elevation={2}>
        {!error ? (
          <Box>
            <CircularProgress className={classes.progress} />
            <Typography variant="h6">Signing in</Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.message}
            >
              {message}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" color="error" gutterBottom>
              Unable to sign in
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {error}
            </Typography>
            {onRetry && (
              <Typography
                variant="body2"
                color="primary"
                style={{ cursor: 'pointer' }}
                onClick={onRetry}
              >
                Try again
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ServerPreparingPage;
